import { on } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import { createImmerReducer } from 'ngrx-immer/store';
import { mergeObjects } from '../../functions/utilities/merge-objects';
import { actionsEffectsActions, actionsFacadeActions, outcomeAssessmentsActions } from './actions.actions';
import { ActionsState } from './actions.types';

export const actionsInitialState: ActionsState = {
  actionsList: null,
  actionsLoading: false,
  actionsLoadingError: false,

  hasCreateActionError: false,
  hasDeleteActionError: false,

  outcomeAssessmentsMap: {},
};

export const actionsReducer = createImmerReducer(
  actionsInitialState,

  on(actionsFacadeActions.loadActions, (state) => ({
    ...state,
    actionsLoading: true,
    actionsLoadingError: false,
  })),

  on(actionsEffectsActions.loadedActions, (state, { actionsList }) => ({
    ...state,
    actionsList,
    actionsLoading: false,
    actionsLoadingError: false,
  })),
  on(actionsEffectsActions.loadingActionsFailed, (state) => ({
    ...state,
    actionsLoading: false,
    actionsLoadingError: true,
  })),
  on(actionsEffectsActions.savedAction, (state, { savedAction }) => {
    const updatedActionsList =
      state.actionsList?.map((action) => {
        if (action?.id === savedAction?.id) {
          const updatedAction = cloneDeep(action);
          mergeObjects(updatedAction, savedAction, true);
          return updatedAction;
        }
        return action;
      }) ?? [];

    return {
      ...state,
      actionsList: updatedActionsList,
    };
  }),

  on(actionsEffectsActions.createActionSuccess, (state, { action }) => ({
    ...state,
    actionsList: [...(state.actionsList ?? []), action],
  })),
  on(actionsEffectsActions.createActionFailed, (state) => ({
    ...state,
    hasCreateActionError: true,
  })),

  on(actionsEffectsActions.deleteActionSuccess, (state, { actionId }) => {
    return {
      ...state,
      actionsList: (state.actionsList ?? []).filter((action) => action?.id !== actionId),
    };
  }),
  on(actionsEffectsActions.deleteActionFailed, (state) => ({
    ...state,
    hasDeleteActionError: true,
  })),

  on(actionsEffectsActions.resetState, (state) => ({
    ...actionsInitialState,
  })),

  on(outcomeAssessmentsActions.loadOutcomeAssessmentsSuccess, (state, { actionId, outcomeAssessments }) => ({
    ...state,
    outcomeAssessmentsMap: {
      ...state.outcomeAssessmentsMap,
      [actionId]: outcomeAssessments,
    },
  })),

  on(
    outcomeAssessmentsActions.savedOutcomeAssessment,
    (state, { actionId, transitionElementId, savedOutcomeAssessment }) => ({
      ...state,
      outcomeAssessmentsMap: {
        ...state.outcomeAssessmentsMap,
        [actionId]: (state.outcomeAssessmentsMap?.[actionId] ?? []).map((o) =>
          o.transitionElementId === transitionElementId
            ? {
                ...o,
                impactType: savedOutcomeAssessment.impactType,
                description: savedOutcomeAssessment.description,
                values: savedOutcomeAssessment.values,
              }
            : o
        ),
      },
    })
  ),

  on(outcomeAssessmentsActions.createOutcomeAssessmentSuccess, (state, { actionId, createdOutcomeAssessment }) => ({
    ...state,
    outcomeAssessmentsMap: {
      ...state.outcomeAssessmentsMap,
      [actionId]: [...(state.outcomeAssessmentsMap?.[actionId] ?? []), createdOutcomeAssessment],
    },
  })),

  on(outcomeAssessmentsActions.deleteOutcomeAssessmentSuccess, (state, { actionId, outcomeAssessmentId }) => ({
    ...state,
    outcomeAssessmentsMap: {
      ...state.outcomeAssessmentsMap,
      [actionId]: (state.outcomeAssessmentsMap?.[actionId] ?? []).filter((o) => o.id !== outcomeAssessmentId),
    },
  })),

  on(outcomeAssessmentsActions.deleteTransitionElementSuccess, (state, { transitionElementId }) => ({
    ...state,
    outcomeAssessmentsMap: Object.fromEntries(
      Object.entries(state.outcomeAssessmentsMap).map(([actionId, outcomeAssessments]) => [
        actionId,
        outcomeAssessments.filter((o) => o.transitionElementId !== transitionElementId),
      ])
    ),
  })),

  on(outcomeAssessmentsActions.deleteActionSuccess, (state, { actionId }) => ({
    ...state,
    outcomeAssessmentsMap: Object.fromEntries(
      Object.entries(state.outcomeAssessmentsMap).filter(([key]) => key !== actionId)
    ),
  })),

  on(
    outcomeAssessmentsActions.patchedActionConnectionsSuccess,
    (state, { actionId, remainingTransitionElementIds }) => ({
      ...state,
      outcomeAssessmentsMap: {
        ...state.outcomeAssessmentsMap,
        [actionId]: (state.outcomeAssessmentsMap?.[actionId] ?? []).filter((o) =>
          remainingTransitionElementIds.includes(o.transitionElementId)
        ),
      },
    })
  ),

  on(outcomeAssessmentsActions.clearOutcomeAssessments, (state) => ({
    ...state,
    outcomeAssessmentsMap: {},
  }))
);
